import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import { TabBar } from './TabBar.js';
import { TabContent } from './TabContent.js';
import './FlowObserverPanel.css';

interface FlowObserverPanelProps {
  theme?: 'light' | 'dark';
}

const PANEL_TITLE = 'FlowObserver';
const LAST_TAB_KEY = 'flowObserver.lastActiveTab';

// ── Docked ────────────────────────────────────────────────────────────────────

const DockedPanel: React.FC = () => {
  const dockedHeight = useFlowObserverPanelStore((s) => s.dockedHeight);
  const activeTabId = useFlowObserverPanelStore((s) => s.activeTabId);
  const constraints = useFlowObserverPanelStore((s) => s.constraints);
  const setDockedHeight = useFlowObserverPanelStore((s) => s.setDockedHeight);
  const setPanelMode = useFlowObserverPanelStore((s) => s.setPanelMode);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);
  const rafId = useRef<number | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = dockedHeight;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      // Batch to one update per animation frame — raw mousemove fires far faster
      // than the canvas can repaint, and applying every event synchronously made
      // the workflow canvas's internal pane lag behind its shrinking/growing
      // container, briefly overlapping the panel with a transparent gap.
      if (rafId.current !== null) return;
      const clientY = e.clientY;
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        const delta = startY.current - clientY;
        setDockedHeight(startH.current + delta);
      });
    };
    const onUp = () => {
      dragging.current = false;
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, [setDockedHeight]);

  // Persist active tab to localStorage
  useEffect(() => {
    if (activeTabId) {
      localStorage.setItem(LAST_TAB_KEY, activeTabId);
    }
  }, [activeTabId]);

  const maxH = Math.floor((window.innerHeight * constraints.dockedMaxHeightVh) / 100);

  return (
    <div
      className="fop-docked"
      style={{ height: dockedHeight }}
      role="region"
      aria-label="FlowObserver Panel"
    >
      <div
        className="fop-resize-handle"
        onMouseDown={onMouseDown}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize panel"
        aria-valuemin={constraints.dockedMinHeight}
        aria-valuemax={maxH}
        aria-valuenow={dockedHeight}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') setDockedHeight(dockedHeight + 10);
          if (e.key === 'ArrowDown') setDockedHeight(dockedHeight - 10);
        }}
      />
      <TabBar panelMode="docked" onSetMode={setPanelMode} />
      <TabContent activeTabId={activeTabId} />
    </div>
  );
};

// ── Collapsed ─────────────────────────────────────────────────────────────────

const CollapsedPanel: React.FC = () => {
  const setPanelMode = useFlowObserverPanelStore((s) => s.setPanelMode);
  return (
    <div className="fop-collapsed" role="region" aria-label="FlowObserver Panel (collapsed)">
      <TabBar panelMode="collapsed" onSetMode={setPanelMode} />
    </div>
  );
};

// ── Fullscreen ────────────────────────────────────────────────────────────────

const FullscreenPanel: React.FC = () => {
  const activeTabId = useFlowObserverPanelStore((s) => s.activeTabId);
  const setPanelMode = useFlowObserverPanelStore((s) => s.setPanelMode);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelMode('docked');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setPanelMode]);

  return (
    <div className="fop-fullscreen" role="region" aria-label="FlowObserver Panel (fullscreen)">
      <TabBar panelMode="fullscreen" onSetMode={setPanelMode} />
      <TabContent activeTabId={activeTabId} />
    </div>
  );
};

// ── Floating ──────────────────────────────────────────────────────────────────

const FloatingPanel: React.FC = () => {
  const geo = useFlowObserverPanelStore((s) => s.floatingGeometry);
  const activeTabId = useFlowObserverPanelStore((s) => s.activeTabId);
  const setFloatingGeometry = useFlowObserverPanelStore((s) => s.setFloatingGeometry);
  const setPanelMode = useFlowObserverPanelStore((s) => s.setPanelMode);

  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const resizing = useRef<'right' | 'bottom' | 'corner' | null>(null);
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });

  const onTitleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: geo.x, py: geo.y };
    e.preventDefault();
  }, [geo.x, geo.y]);

  const onResizeMouseDown = useCallback((e: React.MouseEvent, edge: 'right' | 'bottom' | 'corner') => {
    resizing.current = edge;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: geo.width, h: geo.height };
    e.preventDefault();
    e.stopPropagation();
  }, [geo.width, geo.height]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (dragging.current) {
        const dx = e.clientX - dragStart.current.mx;
        const dy = e.clientY - dragStart.current.my;
        setFloatingGeometry({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
      }
      if (resizing.current) {
        const dx = e.clientX - resizeStart.current.mx;
        const dy = e.clientY - resizeStart.current.my;
        const update: Partial<typeof geo> = {};
        if (resizing.current === 'right' || resizing.current === 'corner') {
          update.width = resizeStart.current.w + dx;
        }
        if (resizing.current === 'bottom' || resizing.current === 'corner') {
          update.height = resizeStart.current.h + dy;
        }
        setFloatingGeometry(update);
      }
    };
    const onUp = () => {
      dragging.current = false;
      resizing.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [setFloatingGeometry]);

  if (geo.minimized) {
    return createPortal(
      <div
        className="fop-floating"
        style={{ left: geo.x, top: geo.y, width: geo.width }}
        role="dialog"
        aria-label="FlowObserver Panel"
        aria-modal="false"
      >
        <div className="fop-floating-titlebar" onMouseDown={onTitleMouseDown}>
          <span className="fop-floating-title">{PANEL_TITLE}</span>
          <div className="fop-floating-controls">
            <button className="fop-icon-btn" title="Restore" onClick={() => setFloatingGeometry({ minimized: false })}>⊟</button>
            <button className="fop-icon-btn" title="Dock" onClick={() => setPanelMode('docked')}>⧉</button>
            <button className="fop-icon-btn" title="Close" onClick={() => setPanelMode('closed')}>×</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className="fop-floating"
      style={{ left: geo.x, top: geo.y, width: geo.width, height: geo.height }}
      role="dialog"
      aria-label="FlowObserver Panel"
      aria-modal="false"
    >
      <div className="fop-floating-titlebar" onMouseDown={onTitleMouseDown}>
        <span className="fop-floating-title">{PANEL_TITLE}</span>
        <div className="fop-floating-controls">
          <button className="fop-icon-btn" title="Minimize" onClick={() => setFloatingGeometry({ minimized: true })}>⊟</button>
          <button className="fop-icon-btn" title="Dock" onClick={() => setPanelMode('docked')}>⧉</button>
          <button className="fop-icon-btn" title="Close" onClick={() => setPanelMode('closed')}>×</button>
        </div>
      </div>
      <TabBar panelMode="floating" onSetMode={setPanelMode} />
      <TabContent activeTabId={activeTabId} />

      <div className="fop-resize-edge fop-resize-edge--right"
        onMouseDown={(e) => onResizeMouseDown(e, 'right')} />
      <div className="fop-resize-edge fop-resize-edge--bottom"
        onMouseDown={(e) => onResizeMouseDown(e, 'bottom')} />
      <div className="fop-resize-edge fop-resize-edge--corner"
        onMouseDown={(e) => onResizeMouseDown(e, 'corner')} />
    </div>,
    document.body
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────

export const FlowObserverPanel: React.FC<FlowObserverPanelProps> = ({ theme: _theme }) => {
  const panelMode = useFlowObserverPanelStore((s) => s.panelMode);

  if (panelMode === 'closed') return null;
  if (panelMode === 'collapsed') return <CollapsedPanel />;
  if (panelMode === 'fullscreen') return <FullscreenPanel />;
  if (panelMode === 'floating') return <FloatingPanel />;
  return <DockedPanel />;
};
